<template>
  <div class="tt-l-masonry" :style="layoutStyles">
    <div class="tt-l-masonry__item"
         v-for="(item, i) in items" :key="i" :style="{ width: tileWidth + 'px' }">
      <slot v-bind:item="item" :index="i"
            :tileWidth="tileWidth" :onTileHeightChange="onTileHeightChange"></slot>
    </div>
  </div>
</template>

<script lang="ts">
import { nextTick } from 'vue'
import bu from 'util/browser-utils'
import { debounce } from 'util/ts-utils'
import domUtils from 'util/dom-utils'
import log from 'util/logger'
import waitForValue from 'util/wait-for-value'

export default {
  name: 'MasonryLayout',
  data() {
    return {
      columnHeights: [],
      breakpoint: null,
      isOnScrollRegistered: false,
      isRepositioning: false,
      lastPositionedIndex: -1,
      maxColHeight: 500,
      numberOfColumns: 1,
      tileWidth: 0,
      tileLeftPositions: []
    }
  },
  emits: ['doneLoading'],
  props: {
    breakpointsConfig: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    useProgressiveLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    layoutStyles() {
      // initial height of 200 will be adjusted as tiles are added
      return { height: `${this.maxColHeight || 200}px` }
    }
  },
  methods: {
    recalculate() {
      // this method determines numberOfColumns and tileWidth
      const containerWidth = this.$parent.$el.clientWidth
      // get the highest breakpoint where viewport is closest, but equal or above the
      // container width
      const bp = this.breakpointsConfig
        .filter(item => containerWidth >= item.viewport)
        .sort((a, b) => a.viewport > b.viewport ? -1 : 1)[0]
      this.breakpoint = bp

      if (bp.numOfCols) {
        // number of columns is specifically set.
        this.numberOfColumns = bp.numOfCols
      } else {
        // number of columns is not specified, it's based on minWidth...
        // In order to figure out the numberOfColumns (x), we need to account for the gutter size
        // being (numberOfColumns - 1) * hSpacing.
        // That produces the following equation:
        // x = (containerWidth - (spacing * (x - 1))) / minWidth
        // Solving for x (numberOfColumns) we get:
        this.numberOfColumns =
          Math.floor((containerWidth + bp.hSpacing) / (bp.minWidth + bp.hSpacing))
      }
      const gutterTotal = (this.numberOfColumns - 1) * bp.hSpacing
      const calcTileWidth = Math.floor((containerWidth - gutterTotal) / this.numberOfColumns)
      // if it chooses maxWidth, means it is too wide and will leave extra space on the right
      // side of the pinboard.
      // TODO: should we make the gutter wider in that case?
      const tileWidth = bp.maxWidth ? Math.min(calcTileWidth, bp.maxWidth) : calcTileWidth
      // If the tileWidth has not changed, there is nothing to do, return false to singify
      // it hasn't changed
      if (tileWidth === this.tileWidth) {
        return false
      }
      this.tileWidth = tileWidth

      // position of left-most column is always zero.  Only gutters between tiles
      this.tileLeftPositions = [0]
      this.columnHeights = [0]
      this.maxColHeight = 0
      for (let i = 1; i < this.numberOfColumns; i++) {
        const previousColLeft = this.tileLeftPositions[i - 1]
        this.tileLeftPositions.push(previousColLeft + this.tileWidth + bp.hSpacing)
        this.columnHeights.push(0) // initialize each column height to zero
      }
      // returning true to signify the tileWidth has changed
      return true
    },
    bindResize() {
      // remove event first so we don't accidentally add it twice
      window.removeEventListener('resize', this.onResize, false)
      window.addEventListener('resize', this.onResize, false)
    },
    bindScroll() {
      // make sure listener isn't already set
      this.removeScrollListener()
      if (this.useProgressiveLoading) {
        // load more on scroll to bottom
        window.addEventListener('scroll', this.onScroll, false)
        this.isOnScrollRegistered = true
      }
    },
    getMaxColHeight() {
      return this.columnHeights.reduce((max, val) => {
        return max > val ? max : val
      })
    },
    getMinHeightIndex() {
      let minIndex = 0
      for (let i = 1; i < this.columnHeights.length; i++) {
        if (this.columnHeights[minIndex] > this.columnHeights[i]) {
          minIndex = i
        }
      }
      return minIndex
    },
    onScroll: debounce(function cb() {
      if (this.isRepositioning) {
        return
      }
      // load more on scroll to bottom of the pinboard
      const pinboardRect = this.$el.getBoundingClientRect()
      const topPlusHeight = pinboardRect.top + pinboardRect.height
      if (topPlusHeight >= 0 && bu.viewportHeight() >= topPlusHeight) {
        this.removeScrollListener()
        // we've reached the bottom, notify the component using this layout that it can load more
        this.$emit('loadMore')
      } else if (!this.isOnScrollRegistered) {
        this.bindScroll()
      }
    }, 100),
    removeScrollListener() {
      this.isOnScrollRegistered = false
      window.removeEventListener('scroll', this.onScroll, false)
    },
    positionTile(tiles, tileIndex) {
      return new Promise<void>((resolve, reject) => {
        if (!this.tileWidth || !this.breakpoint) {
          // not initialized through recalculate() yet
          resolve()
          return
        }
        const tile = tiles[tileIndex]
        const colIndex = this.getMinHeightIndex()
        tile.style.left = `${this.tileLeftPositions[colIndex]}px`
        tile.style.top = `${this.columnHeights[colIndex]}px`

        // making sure the changes to tile are reflected in the UI before calculating height
        this.onTileReady(tile).then(() => {
          // waiting until the tile is ready to display it and determine it's height
          tile.style.opacity = 1
          this.lastPositionedIndex = tileIndex
          const newHeight =
            this.columnHeights[colIndex] + tile.offsetHeight + this.breakpoint.vSpacing
          this.setNewColHeight(colIndex, newHeight)
          const nextTileIndex = tileIndex + 1
          if (tiles.length > nextTileIndex) {
            this.positionTile(tiles, nextTileIndex).then(() => {
              resolve()
            }).catch(() => {
              reject()
            })
          } else {
            // no more to position, go ahead and resolve
            resolve()
          }
        }).catch(() => {
          // skip this one
          const nextTileIndex = tileIndex + 1
          if (tiles.length > nextTileIndex) {
            this.positionTile(tiles, nextTileIndex).then(() => {
              resolve()
            }).catch(() => {
              reject()
            })
          }
        })
      })
    },
    // should only be called through on tilesAdded or onResize to help avoid too many calls
    positionTiles: debounce(function cb(fromResize) {
      if (this.isRepositioning) {
        return
      }
      this.isRepositioning = true
      if (this.tileWidth <= 0) {
        waitForValue(() => {
          return this.tileWidth > 0
        }).then(()  => this.positionTiles(fromResize))
          .catch(() => {
            log.warn('Tile width not set')
          })
        return
      }
      try {
        const tiles = this.getTiles()
        if (tiles.length > 0) {
          if (fromResize) {
            this.lastPositionedIndex = -1
          }
          const startingIndex = this.lastPositionedIndex + 1
          const tilesToPosition = tiles
          // positionTile is a recursive method so it can handle waiting for onTileReady before
          // positioning the next tile
          this.positionTile(tilesToPosition, startingIndex).then(() => {
            // just in case the maxHeight is actually supposed to be less than it was before
            this.maxHeight = this.getMaxColHeight()
            // Notify the component using this layout, that it is done
            this.$emit('doneLoading')
            // nextTick is used to wait for doneLoading to have time
            nextTick(() => {
              // once all tiles are positioned, we can allow positionTiles to be called again
              // when checking for isRepositioning
              this.isRepositioning = false
              // TT-11340. Just in case the user is already positioned at the bottom of the pinboard
              // with progressive loading on, see if more need to be loaded
              if (this.useProgressiveLoading) {
                this.onScroll()
              }
            })
          }).catch(() => {
            // just in case
            this.isRepositioning = false
          })
        } else {
          this.isRepositioning = false
        }
      } catch (e) {
        this.isRepositioning = false
      }
    }, 100),
    pushColumnDown(tile) {
      // using the tile's left position, we can tell which column it is in, then we can push down
      // all the other tiles that are in the same column (based on their left as well).
      const leftPos = tile.style.left
      const colIndex = this.tileLeftPositions.indexOf(parseInt(leftPos, 10))
      // the column height if this were the last tile in the column.  It is the position of the
      // bottom of the tile including the padding below it
      const columnHeightFromThisTile =
        parseInt(tile.style.top, 0) + tile.offsetHeight + this.breakpoint.vSpacing
      const nextTilesInCol = this.getTilesFromSameColAfterTile(tile)
      let heightDifference = 0 // the heightDifference is how much each tile needs to move down
      if (nextTilesInCol.length > 0) {
        const nextTile = nextTilesInCol[0]
        const nextTileTop = parseInt(nextTile.style.top, 10)
        heightDifference = columnHeightFromThisTile - nextTileTop
      }
      if (heightDifference) {
        // move tiles down
        for (let i = 0; i < nextTilesInCol.length; i++) {
          const nextTile = nextTilesInCol[i]
          const thisTop = parseInt(nextTile.style.top, 10)
          nextTile.style.top = `${(thisTop + heightDifference)}px`
        }
        // now that tiles have moved down, set the new column height
        this.setNewColHeight(colIndex, this.columnHeights[colIndex] + heightDifference)
      } else {
        // if heightDifference still hasn't been set, must be last tile in column
        this.setNewColHeight(colIndex, columnHeightFromThisTile)
      }
    },
    setNewColHeight(colIndex, newHeight) {
      this.columnHeights.splice(colIndex, 1, newHeight)
      const maxHeight = this.getMaxColHeight()
      if (this.maxColHeight < maxHeight) {
        this.maxColHeight = maxHeight
      }
    },
    getTiles() {
      return this.$el.querySelectorAll('.tt-l-masonry__item')
    },
    getTilesFromSameColAfterTile(tile) {
      const colTiles = []
      let nextTile = tile.nextElementSibling
      while (nextTile) {
        if (nextTile.style.left === tile.style.left) { // same column
          colTiles.push(nextTile)
        }
        nextTile = nextTile.nextElementSibling
      }
      return colTiles
    },
    onResize: debounce(function cb() {
      if (this.recalculate()) {
        this.positionTiles(true)
      }
    }, 200),
    onTileHeightChange(el) {
      if (el) {
        nextTick(() => {
          this.pushColumnDown(el.parentElement)
        })
      }
    },
    onTileReady(tile) {
      // checks for existence of img tag.  If present, makes sure it's loaded.
      return new Promise<void>((resolve, reject) => {
        nextTick(() => {
          const img = tile.querySelector('img') as  HTMLImageElement
          if (!img) {
            resolve()
            return
          }
          if (img.complete && img.naturalHeight !== 0) {
            resolve()
            return
          }
          const loadedFn = () => {
            this.unbindImgListeners(img, loadedFn, errorFn)
            resolve()
          }
          const errorFn = () => {
            this.unbindImgListeners(img, loadedFn, errorFn)
            reject()
          }
          img.addEventListener('load', loadedFn)
          img.addEventListener('error', errorFn)
          // check one last time
          if (img.complete) {
            this.unbindImgListeners(img, loadedFn, errorFn)
            if (img.naturalHeight !== 0) {
              resolve()
            } else if (!img.naturalHeight) {
              reject()
            }
          }
        })
        return
      })
    },
    unbindImgListeners(img: HTMLImageElement, loadFn, errorFn) {
      img.removeEventListener('load', loadFn)
      img.removeEventListener('error', errorFn)
    }
  },
  mounted() {
    domUtils.whenElementIsVisible(() => this.$el).then(() => {
      this.recalculate()
      this.bindScroll()
      this.positionTiles()
    }).catch(() => {})
    this.bindResize()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize, false)
    this.removeScrollListener()
  },
  watch: {
    items: {
      handler(val) {
        if (val.length > 0) {
          this.positionTiles()
        }
      },
      deep: true
    },
    useProgressiveLoading() {
      if (this.useProgressiveLoading) {
        this.bindScroll()
      } else {
        this.removeScrollListener()
      }
    }
  }
}
</script>

<style lang="scss" src="./masonry.scss"></style>
